import {_, action, classNames, computed, Fragment, IDisposer, observer, Popover2, TimeAgo, React, useAsObservableSource, useEffect} from '@api'
import {Checkbox, Tooltip} from '@material-ui/core'
import {ArrowRight, KeyboardArrowRight} from '@material-ui/icons'
import {ChangeLogFlexLayoutTabStore, ChangeLogItemsStore, ChangeLogSingleItemMoreOptions, ChangeLogSingleItemStore, ChangeLogSingleItemStoreContext, ISingleChangeItem, ChangeLogOperationTypes, useChangeLogFlexLayoutTabStore, useChangeLogItemsStore, useVipLocalStore, utils, VipLocalStore, ICatalogItemChangeEvent, BuilderCard} from '@modules'
import {ICatalogFieldConfig, StoresInstance, Workspace} from '@stores'
import {observable, toJS, when} from 'mobx'
import moment from 'moment'
import {ChangeEntryCatalogItem} from '.'
import * as css from './ChangeLogEntriesForCatalogItemAtTimestamp.css'
import {ChangeLogEntryIndividual} from './ChangeLogEntryIndividual'

export type ChangeLogEntryByField = { item: ICatalogItemChangeEvent, changed: IChangeLogChanged }

interface MyProps {
  items: Array<ICatalogItemChangeEvent>
  catalogId: string

  className?: string
  // changed?: ISingleChangeItem
  nested?: string
  label?: string
  index?: number
}

export interface IChangeLogChanged {
  diff: { before, after },
  field: string
  id: string
  op: ChangeLogOperationTypes
  nested?: boolean
  label?: string
}

export class ChangeLogEntriesForCatalogItemAtTimestampStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { root: ChangeLogFlexLayoutTabStore }) {
    super(stores)
  }

  get root() { return this.props.root}

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @computed get sortedItems(): Array<ChangeLogEntryByField> {
    const {flattenedItems, workspace} = this

    if (!workspace) return flattenedItems

    const {workspace: {catalog: {_fieldByName, labelForFieldName_currentCatalog: labelFor}}} = this

    return _.sortBy(flattenedItems, ({item, changed}) => {
      if (changed.field) {
        const {field, nested} = changed,
          fieldLabel = labelFor(field)

        return nested ? `${changed.label} ${nested}'s ${fieldLabel}` : fieldLabel
      }
      return null
    })
  }

  @action openItemDetails = (e: React.MouseEvent) => {
    const {workspace, id, flexLayout: {activeOrLastCatalogRoot, create}} = this
    create.createItemDetailsTab({
      tabSet: activeOrLastCatalogRoot?.tabSet,
      workspace: workspace._id,
      selectNewTab: true,
      path: [{esIndex: workspace.catalog.index, where: [{value: id}]}]
    })

    e.stopPropagation()
  }

  @computed get flattenedItems(): Array<ChangeLogEntryByField> {
    const {props: {items}, root: {shouldApplyFilters, filteredFields}, workspace: {catalog: {sectionById, fieldBySection, sectionValuesFor}}} = this
    const r = items.flatMap(v => {
      return v._log.changes.filter(change => !!change).reduce((acc, change) => {
        const section = sectionById[change.field],
          fields = fieldBySection[change.field]

        if (!!section) {
          const values = sectionValuesFor(change.field),
            before = utils.isJson(change.diff?.before) ? JSON.parse(change.diff.before) : [],
            after = utils.isJson(change.diff?.after) ? JSON.parse(change.diff.after) : []
          if (values !== null) {
            return [
              ...acc,
              ..._.flatMap(values.map(({key}) => {
                const beforeObject = before.find(b => _.get(b, section.viewOptions.field) === key),
                  afterObject = after.find(a => _.get(a, section.viewOptions.field) === key)
                return fields.map(field => {
                  const f = field.field.replace(`${change.field}.`, ''),
                    a = _.get(afterObject, f),
                    b = _.get(beforeObject, f)
                  if (a !== b) {
                    return {
                      changed: {
                        ...change,
                        id: `${change.id}_${key}_${f}`,
                        field: field.field,
                        diff: {
                          before: b,
                          after: a
                        }
                      },
                      label: section.label,
                      nested: key,
                      item: v
                    }
                  }
                }).filter(_.identity)
              }))
            ]
          }
        }

        return [
          ...acc,
          {changed: change, item: v}
        ]
      }, [])
    })

    if (shouldApplyFilters && filteredFields.size > 0) {
      return r.filter(v => filteredFields.has(v.changed.field))
    }

    return r
  }

  get id() {
    return this.props.catalogId
  }

  get items() { return this.props.items}

  @computed get firstItem(): ICatalogItemChangeEvent {
    return _.first(this.items)
  }

  get log() {
    return this.firstItem._log
  }

  @action onPopoverInteraction = (nextState) => {
    const {root, root: {popoverItem}} = this
    if (popoverItem && !nextState) {
      root.popoverItem = null
    }
  }

  @computed get workspace(): Workspace {
    const {log, workspaces: {workspaceByIndex}} = this
    return _.first(workspaceByIndex[log.index])
  }

  @computed get dataMap() {
    const {workspace} = this
    if (!workspace) {
      return null
    }
    return workspace.dataMap
  }

  //
  // @computed get field() {
  //   if (!this.workspace) {
  //     return null
  //   }
  //   const {workspace: {catalog: {_fieldByName}}, changed} = this
  //   return _fieldByName[changed.field]
  // }

  get nested() {
    return this.props.nested
  }

  get label() {
    return this.props.label
  }

  @observable isChecked = false
  @action onToggleCheck = () => {
    this.isChecked = !this.isChecked
  }

  @action onClickOpenInSidebar = () => {
    const {firstItem: item, workspace, stores: {sidebar: {right}}, catalog} = this,
      path = catalog.pathForData(null, item, workspace?.config)

    right.updateState({open: true})
    right.path = path.slice()
    catalog.focusedCatalogItemId = catalog.idFor(item)
  }

  @action onClickOpenWorkspace = (e) => {
    const {firstItem: item, workspace, stores: {flexLayout, flexLayout: {create}}} = this,
      tab = create.newCatalogTab({workspace: workspace.config, selectNewTab: true})
  }

  @action onClickMoveToSeason = (e?: React.MouseEvent) => {
    const {workspace, stores: {flexLayout: {create}}} = this,
      tab = create.newCatalogTab({
        workspace: workspace.config, selectNewTab: true
      })

    setTimeout(() => {
      const {catalogRoot: {catalog}} = tab, {firstItem: item} = this
      when(() => catalog.hasLoadedConfig, () => {
        catalog.navigateToHierarchy(item)
      })
    }, 2000)
  }

  @computed get checked() {
    const {root: {_selected}, id} = this
    return _selected.includes(id)
  }

  @computed get timestamp() {
    const {log: {timestamp}} = this

    return moment(timestamp)
  }

  @computed get parentFields() {
    const {catalog: {_fieldByName, features: {changelog: {parents}}}} = this

    return (parents ?? []).map(f => _fieldByName[f]).filter(_.identity)
  }

  @computed get actionLabel() {
    const {firstItem: {_log: {operation}}} = this
    switch (operation) {
      case 'CREATE':
        return 'Created'

      case 'DELETE':
        return 'Deleted'

      default:
        return null
    }
  }

  @computed get itemName() {
    const {firstItem: item, dataMap: {title}, root: {config}, stores} = this
    return _.isFunction(config?.title) ? config.title(item, title, stores) : _.get(item, title)
  }

  @computed get userInfo() {
    const {stores, log} = this, {users: {getById}} = stores,
      userInfo = getById(log.userId)

    return userInfo.name
  }
}

export const ChangeLogEntriesForCatalogItemAtTimestampStoreContext = React.createContext<ChangeLogEntriesForCatalogItemAtTimestampStore>(null)
export const useChangeLogEntriesForCatalogItemAtTimestampStore = () => React.useContext(ChangeLogEntriesForCatalogItemAtTimestampStoreContext)

export const ChangeLogEntriesForCatalogItemAtTimestamp = observer<MyProps>(function ChangeLogEntriesForCatalogItemAtTimestamp({className, ...props}) {
  const root = useChangeLogFlexLayoutTabStore(),
    obsProps = useAsObservableSource({...props, root}),
    store = useVipLocalStore((stores) => new ChangeLogEntriesForCatalogItemAtTimestampStore(stores, obsProps))

  if (!store.workspace) {
    return null
  }
  const {
    checked, root: {popoverItem}, onPopoverInteraction,
    props: {index}, sortedItems, openItemDetails, timestamp, firstItem: item, userInfo
  } = store

  useEffect(() => store.registerEffects(), [])

  // console.log({props, operation, items, flattenedItems})
  if (_.isEmpty(sortedItems)) return null

  //data-palette-index={paletteIndex}
  //
  return (
    <ChangeLogEntriesForCatalogItemAtTimestampStoreContext.Provider value={store}>
      <Popover2
        interactionKind='click'
        placement='auto'
        isOpen={popoverItem?.changeEvent == store}
        onInteraction={onPopoverInteraction}
        minimal={false}
        content={
          popoverItem && <BuilderCard
			              data={popoverItem.data}
			              showImage
			              isPopover
			              workspace={popoverItem.workspace.config}
			              visibleCardFields={popoverItem.workspace.defaultVisibleCardFields}
		              />}

      >
        <div className={classNames(css.root, className)}
             data-index={index}
             data-selected={checked}
             onDoubleClick={openItemDetails}
             data-multiline={sortedItems.length > 1}
        >

          {/* What item is being changed? */}
          <CatalogItemChangeEntry/>

          {/* What fields were changed for the above item? */}
          <div className={css.itemChanges}>
            {/*<span>{flattenedItems.length} {pluralize('Entry', items.length)}</span>*/}
            {sortedItems.map((v, i) => {
              return <ChangeLogEntryIndividual
                item={v}
                includeTimestampAndUser={sortedItems.length === 1}
                index={i}
                key={i}
              />
            })}
          </div>
        </div>
      </Popover2>
    </ChangeLogEntriesForCatalogItemAtTimestampStoreContext.Provider>)
})

export const CatalogItemChangeEntry = observer(function CatalogItemChangeEntry() {
  const {
    actionLabel, parentFields, root: {trackCurrentWorkspace},
    workspace: {catalog: {catalogItemName}},
    isChecked, onToggleCheck, firstItem, timestamp, sortedItems,
    userInfo
  } = useChangeLogEntriesForCatalogItemAtTimestampStore()

  return (<Fragment>
    <div className={css.catalogItemChangeHeader} data-type='CatalogItemHeader'>
      {false && <Checkbox
		  data-checkbox='accordion'
		  checked={isChecked}
		  className={css.check}
		  data-checked={isChecked}
		  size='small'
		  classes={{colorSecondary: 'transparent'}}
		  onChange={onToggleCheck}
	  />}
      {parentFields.map(f => <Fragment key={f.field}>
        <ChangeEntryCatalogItem key={f.field} field={f}/>
        <KeyboardArrowRight/>
      </Fragment>)}
      <ChangeEntryCatalogItem/>

      {actionLabel && <span className={css.actionLabel}>{actionLabel}</span>}

      {!trackCurrentWorkspace && <span data-type='workspace-badge'>{catalogItemName}</span>}

      {sortedItems.length > 1 && <div className={css.timestampAndUser}>
        {false && <TimeAgo className={css.timestamp} date={firstItem._log.timestamp}/>}

		  <Tooltip title={timestamp.format('MM/DD/YYYY')}>
			  <span data-type='timestamp'>{timestamp.format('hh:mm A')}</span>
		  </Tooltip>
		  <span className={css.username}>({userInfo})</span>
	  </div>}
    </div>
  </Fragment>)
})



// WEBPACK FOOTER //
// ./src/modules/changelog/entries/ChangeLogEntriesForCatalogItemAtTimestamp.tsx